import { useState } from "react"

import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import {
	Stripe,
	StripeElements,
	StripeError,
	StripePaymentElementChangeEvent,
	StripePaymentElementOptions,
} from "@stripe/stripe-js"

import "@/lib/styles/index.css"
import styles from "./Payment.module.scss"
import { isValidEmailForPayment, isValidNameForPayment } from "./logic"

import apiService from "@/provider/rest"

export type PaymentMethodType =
	| "card"
	| "bank_transfer"
	| "konbini"
	| "apple_pay"
	| "google_pay"

type Props = {
	amount: number
	paymentID: string
	name: string
	phoneNumber: string
	email: string
	paymentMethod: PaymentMethodType
	onChangePaymentMethod: (paymentMethodType: PaymentMethodType) => void
	konbiniHostedVoucherUrl: string | null
	konbiniClientSecret: string | undefined
	isPartialPayment: boolean
	showKonbiniVoucherButton: boolean
}

const onChange = (
	event: StripePaymentElementChangeEvent,
	onChangePaymentMethod: (paymentMethodType: PaymentMethodType) => void
) => {
	console.log(event.value.type)
	if (event.value.type === "card") {
		onChangePaymentMethod("card")
	} else if (event.value.type === "customer_balance") {
		onChangePaymentMethod("bank_transfer")
	} else if (event.value.type === "konbini") {
		onChangePaymentMethod("konbini")
	} else if (event.value.type === "apple_pay") {
		onChangePaymentMethod("apple_pay")
	} else if (event.value.type === "google_pay") {
		onChangePaymentMethod("google_pay")
	} else {
		console.log("unknown event type", event.value.type)
	}
}

const errorMessages: Record<string, string | Record<string, string>> = {
	card_declined: {
		insufficient_funds: "カードの残高が不足しています。",
		card_velocity_exceeded: "利用頻度の上限を超過しています。",
		generic_decline: "カードが拒否されました。",
	},
	expired_card: "カードの有効期限が間違っているか、期限が過ぎています。",
	incorrect_cvc: "セキュリティーコードの誤りがあります。",
	incorrect_number: "カードの番号に誤りがあります。",
}

const confirmPayment = async (
	stripe: Stripe,
	elements: StripeElements,
	props: Props
): Promise<StripeError | undefined> => {
	let return_url =
		process.env.REACT_APP_WEB_URL + `/payment/complete/${props.paymentID}`
	if (props.paymentMethod === "bank_transfer") {
		// 振込先情報がかかれたモーダルを閉じた後、complete画面にredirectさせない
		return_url =
			process.env.REACT_APP_WEB_URL +
			`/payment/${props.paymentID}?email=${props.email}`
	}

	const result = await stripe.confirmPayment({
		elements,
		confirmParams: {
			return_url,
			payment_method_data: {
				billing_details: {
					name: props.name,
					email: props.email,
					address: {
						country: "JP",
					},
				},
			},
		},
	})
	return result.error ? result.error : undefined
}

// コンビニ決済用の確認関数（別のPaymentIntentを使用するため、Elementsを使わない）
const confirmKonbiniPayment = async (
	stripe: Stripe,
	props: Props
): Promise<StripeError | undefined> => {
	if (!props.konbiniClientSecret) {
		return {
			type: "api_error",
			message: "コンビニ決済の設定がありません",
		} as StripeError
	}

	const return_url =
		process.env.REACT_APP_WEB_URL +
		`/payment/${props.paymentID}?email=${props.email}`

	// 電話番号を入力することで、店内の UI で支払いコードと確認番号の入力が求められる支払いプロセスが簡素化されるらしいので、いれておいてあげる
	// ref: https://docs.stripe.com/payments/konbini/accept-a-payment?locale=ja-JP
	const result = await stripe.confirmKonbiniPayment(props.konbiniClientSecret, {
		payment_method: {
			billing_details: {
				name: props.name,
				email: props.email,
				phone: props.phoneNumber,
			},
		},
		// payment_method_options: {
		// 	konbini: {
		// 		confirmation_number: props.paymentID.slice(0, 10),
		// 	},
		// },
		return_url,
	})
	return result.error ? result.error : undefined
}

const getErrorMessage = (error: StripeError): string => {
	const message = errorMessages[error.code ?? ""]
	if (typeof message === "string") {
		// expired_card, incorrect_number, incorrect_cvc の場合
		return message
	} else if (message && error.decline_code && message[error.decline_code]) {
		// card_declined の場合、更に下層のdecline_codeを見る
		return message[error.decline_code]
	}
	return "エラーが発生しました。"
}

export default function StripeForm(props: Props) {
	const stripe = useStripe()
	const elements = useElements()

	const [isLoading, setIsLoading] = useState(false)

	const handleSubmit = async (e) => {
		e.preventDefault()

		if (
			!stripe ||
			!elements ||
			!isValidEmailForPayment(props.paymentMethod, props.email) ||
			!isValidNameForPayment(props.paymentMethod, props.name)
		) {
			// Stripe.js hasn't yet loaded.
			// Make sure to disable form submission until Stripe.js has loaded.
			return
		}

		setIsLoading(true)
		if (props.email !== "") {
			// TODO: mutate
			await apiService.put("/public/payment/customer/" + props.paymentID, {
				email: props.email,
			})
		}

		try {
			const error =
				props.paymentMethod === "konbini"
					? await confirmKonbiniPayment(stripe, props) // コンビニ決済は別のPaymentIntentを使用するため、専用の確認関数を使う
					: await confirmPayment(stripe, elements, props)
			if (!error) {
				return
			}
			alert(
				getErrorMessage(error) +
					"カード情報を確認するか、別のクレジットカードをお試しください。"
			)
			console.log("confirmPayment error", error)
		} catch (err) {
			console.error("Error in stripe.confirmPayment: ", err)
		} finally {
			setIsLoading(false)
		}
	}

	const paymentElementOptions: StripePaymentElementOptions = {
		layout: "auto" as const,
		// apple pay, google pay は card に含まれる
		paymentMethodOrder: ["card", "customer_balance", "konbini"],
		defaultValues: {
			billingDetails: {
				phone: props.phoneNumber,
			},
		},
		terms: {
			usBankAccount: "never" as const,
		},
		fields: {
			billingDetails: {
				name: "never" as const,
				email: "never" as const,
				address: {
					country: "never" as const,
				},
			},
		},
	}

	// 部分支払い済みの場合はコンビニ決済を無効にする
	const isKonbiniDisabledDueToPartialPayment =
		props.paymentMethod === "konbini" && props.isPartialPayment

	return (
		<>
			<form id="payment-form" onSubmit={handleSubmit}>
				<PaymentElement
					id="payment-element"
					options={paymentElementOptions}
					onChange={(event) => onChange(event, props.onChangePaymentMethod)}
				/>
				{isKonbiniDisabledDueToPartialPayment && (
					<div className={styles.partial_payment_warning}>
						一部入金済みのため、コンビニ決済はご利用いただけません。
						<br />
						クレジットカードまたは銀行振込をご利用ください。
					</div>
				)}
				{props.showKonbiniVoucherButton ? (
					<button
						type="button"
						className={`common-button ${styles.payment_button}`}
						onClick={() =>
							window.open(props.konbiniHostedVoucherUrl!, "_blank")
						}
					>
						支払い番号を確認する
					</button>
				) : (
					<button
						type="submit"
						className={`common-button ${styles.payment_button}`}
						disabled={
							isLoading ||
							!stripe ||
							!elements ||
							!isValidEmailForPayment(props.paymentMethod, props.email) ||
							!isValidNameForPayment(props.paymentMethod, props.name) ||
							isKonbiniDisabledDueToPartialPayment
						}
						id="submit"
					>
						{isLoading
							? "処理中..."
							: props.paymentMethod === "bank_transfer"
								? "銀行口座情報を確認する"
								: `${props.amount}円支払う`}
					</button>
				)}
			</form>
		</>
	)
}
